import { CopyType } from '@heidi/schema/ml-scribe';
import { type CopySettings, type CopySettingsWire } from './useCopySettings.types';

/**
 * Map a legacy `CopyType` enum value to the four-toggle `CopySettings`
 * shape introduced by SCR-4287.
 *
 * - PLAIN              -> bold/italics + tables off, bullet points ON
 * - WITHOUT_LISTS      -> bold/italics + tables on, bullet points off
 * - WITH_LISTS         -> bold/italics + tables + bullet points on
 *
 * `headings` defaults off in every branch: a section H1 copies/exports as
 * plain body text so a note looks the same as it did before sections were
 * enabled. Users opt back into the heading via the "Headings" toggle.
 *
 * Why PLAIN keeps `bulletPoints: true`: the legacy `Copy as plain text`
 * implementation (`copyPlainTextV2`) does NOT strip leading `- ` markers
 * — it just dumps the editor's markdown verbatim. Migrating a PLAIN user
 * to `bulletPoints: false` would silently change their clipboard output
 * (dashes would disappear). Faithful migration: keep bullets on; users
 * who actively want dashes gone can toggle bullets off via the new UI.
 *
 * Anything unrecognised falls back to WITHOUT_LISTS (the BE default for
 * `default_copy_type`).
 */
export const enumToToggles = (copyType: CopyType | null | undefined): CopySettings => {
  switch (copyType) {
    case CopyType.COPY_WITH_PLAIN_TEXT:
      return {
        headings: false,
        boldAndItalics: false,
        bulletPoints: true,
        tables: false,
      };
    case CopyType.COPY_WITH_FORMATTING_WITH_LISTS:
      return {
        headings: false,
        boldAndItalics: true,
        bulletPoints: true,
        tables: true,
      };
    case CopyType.COPY_WITH_FORMATTING_WITHOUT_LISTS:
    default:
      return {
        headings: false,
        boldAndItalics: true,
        bulletPoints: false,
        tables: true,
      };
  }
};

/**
 * Convert the snake_case wire payload from `session_settings.copy_settings`
 * (BE post-SCR-4468) to the camelCase FE shape.
 */
export const wireToCopySettings = (wire: CopySettingsWire): CopySettings => ({
  headings: wire.headings,
  boldAndItalics: wire.bold_and_italics,
  bulletPoints: wire.bullet_points,
  tables: wire.tables,
});

/**
 * Convert the camelCase FE shape back to the snake_case wire payload.
 */
export const copySettingsToWire = (settings: CopySettings): CopySettingsWire => ({
  headings: settings.headings,
  bold_and_italics: settings.boldAndItalics,
  bullet_points: settings.bulletPoints,
  tables: settings.tables,
});
